import React, { useEffect, useMemo, useState } from 'react';
import { X, ReceiptText } from 'lucide-react';
import { apiService } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import { UiStyles } from '@/theme/ui';
import { beginGuestSessionClaim } from '@/utils/guestSessionClaim';

interface Props {
  bookingId: string;
  branchCurrency?: string;
  tableName?: string;
  onClose: () => void;
}

interface BillLine { name: string; quantity: number; unitPrice: number; lineTotal: number }
interface Bill {
  items: BillLine[];
  subtotal: number;
  discount: number;
  offerLabel?: string;
  tax: number;
  grandTotal: number;
  currency?: string;
  amountPaid: number;
  amountDue: number;
  paymentStatus?: string;
  // Branch tax / legal identity (all optional; server may omit or send disabled/zeroed)
  vatAmount: number;
  vatRate: number;
  taxLabel?: string; // 'VAT' | 'GST' | … — the branch's tax label
  alcoholTaxAmount: number; // UAE municipality alcohol levy (0 when off)
  alcoholTaxRate: number;   // fraction, e.g. 0.30
  trn?: string;
  invoiceNumber?: string;
  legalEntity?: string;
}

const LS_PENDING_PAYMENT = 'pendingTabPayment';

// Razorpay is loaded dynamically; keep a loose type so TS doesn't need the SDK's types.
declare global {
  interface Window { Razorpay?: any }
}

const num = (v: any): number => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? Number(n) : 0;
};

/** Defensive normaliser: the backend field names may vary slightly, so accept common aliases. */
function normaliseBill(raw: any): Bill {
  const d = raw || {};
  const items: BillLine[] = ((d.items || d.lineItems || []) as any[]).map((it) => ({
    name: it.name || it.itemName || it.label || 'Item',
    quantity: num(it.quantity ?? it.qty ?? 1),
    unitPrice: num(it.unitPrice ?? it.price ?? it.rate),
    lineTotal: num(it.lineTotal ?? it.total ?? it.amount ?? (num(it.unitPrice ?? it.price) * num(it.quantity ?? it.qty ?? 1))),
  }));
  const subtotal = num(d.subtotal ?? d.subTotal ?? items.reduce((s, i) => s + i.lineTotal, 0));
  const discount = num(d.discount ?? d.discountAmount ?? d.discountTotal);
  // Name the offer on the bill: "Happy hour — 20%" reads as something the venue gave you, where a
  // bare "Discount" reads as an unexplained number.
  const offerLabel: string | undefined = d.offer?.label || undefined;
  const tax = num(d.tax ?? d.taxAmount);
  const grandTotal = num(d.grandTotal ?? d.total ?? d.grand_total ?? (subtotal - discount + tax));
  const amountPaid = num(d.amountPaid ?? d.paid ?? d.amount_paid);
  const amountDue = num(d.amountDue ?? d.due ?? d.amount_due ?? (grandTotal - amountPaid));
  // VAT block: prefer nested `vat` object, fall back to top-level aliases.
  const vat = (d.vat && typeof d.vat === 'object') ? d.vat : {};
  const vatAmount = num(vat.vatAmount ?? vat.amount ?? d.vatAmount ?? d.vat_amount ?? d.taxTotal);
  const vatRate = num(vat.rate ?? vat.vatRate ?? d.vatRate ?? d.vat_rate);
  const trn = vat.trn ?? d.trn ?? d.TRN ?? undefined;
  const taxLabel = vat.label ?? vat.taxLabel ?? d.taxLabel ?? d.vat_label ?? undefined;
  const alc = (d.alcoholTax && typeof d.alcoholTax === 'object') ? d.alcoholTax : {};
  const alcoholTaxAmount = num(alc.amount ?? d.alcoholTaxAmount);
  const alcoholTaxRate = num(alc.rate ?? d.alcoholTaxRate);
  return {
    items,
    subtotal,
    discount,
    offerLabel,
    tax,
    grandTotal,
    currency: d.currency || d.currencyCode || undefined,
    amountPaid,
    amountDue,
    paymentStatus: d.paymentStatus || d.status || undefined,
    vatAmount,
    vatRate,
    alcoholTaxAmount,
    alcoholTaxRate,
    taxLabel: taxLabel ? String(taxLabel) : undefined,
    trn: trn ? String(trn) : undefined,
    invoiceNumber: d.invoiceNumber || d.invoiceNo || d.invoice_number || undefined,
    legalEntity: d.legalEntity || d.legal_entity || undefined,
  };
}

const isPaidStatus = (s?: string) => !!s && ['paid', 'settled', 'complete', 'completed'].includes(s.toLowerCase());

/** Build the tax row label from the branch label + rate (rate as fraction 0.05 or percent 5). */
function vatLabel(rate: number, label?: string): string {
  const name = (label && label.trim()) || 'VAT';
  if (!rate) return name;
  const pct = rate <= 1 ? rate * 100 : rate;
  const clean = Number.isInteger(pct) ? String(pct) : String(Number(pct.toFixed(2)));
  return `${name} (${clean}%)`;
}

/** Inject the Razorpay checkout script once, resolving when window.Razorpay is available. */
function loadRazorpay(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const existing = document.getElementById('razorpay-checkout-js') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(!!window.Razorpay));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const s = document.createElement('script');
    s.id = 'razorpay-checkout-js';
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve(!!window.Razorpay);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * BillModal — a bottom-sheet that shows a table's itemized tab and lets the guest pay it (tip
 * optional) via Razorpay, or settle at the counter. When the backend returns a MOCK order (gateway
 * keys unset) we skip the SDK and verify immediately, so the whole flow works without real keys.
 */
/** Post-payment feedback funnel: rate → (high rating) prompt for a Google review. */
const PayFeedback: React.FC<{ bookingId: string }> = ({ bookingId }) => {
  const [rating, setRating] = useState(0);
  const [sent, setSent] = useState(false);
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rate = async (r: number) => {
    setRating(r); if (busy || sent) return; setBusy(true);
    try { const res = await apiService.submitServiceFeedback({ bookingId, rating: r }); setReviewUrl(((res as any)?.data?.reviewUrl) ?? null); }
    catch { /* best-effort */ } finally { setSent(true); setBusy(false); }
  };
  if (sent) {
    return (
      <div style={{ marginTop: 12 }}>
        {reviewUrl
          ? (<><div style={{ fontSize: '0.85rem', marginBottom: 8 }}>🙏 So glad you enjoyed it! Mind sharing a quick review?</div>
              <a href={reviewUrl} target="_blank" rel="noreferrer" style={reviewBtn}>⭐ Leave a Google review</a></>)
          : <div style={{ fontSize: '0.85rem' }}>Thanks for your feedback! 🙏</div>}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: '0.85rem', marginBottom: 6, opacity: 0.9 }}>How was your visit?</div>
      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
        {[1, 2, 3, 4, 5].map((nStar) => (
          <button key={nStar} onClick={() => rate(nStar)} disabled={busy} aria-label={`${nStar} star`}
            style={{ background: 'none', border: 'none', fontSize: 30, cursor: 'pointer', lineHeight: 1, padding: 2, filter: nStar <= rating ? 'none' : 'grayscale(1)', opacity: nStar <= rating ? 1 : 0.55 }}>⭐</button>
        ))}
      </div>
    </div>
  );
};
const reviewBtn: React.CSSProperties = { display: 'inline-block', background: '#fff', color: '#111827', fontWeight: 800, borderRadius: 10, padding: '10px 18px', textDecoration: 'none' };

const BillModal: React.FC<Props> = ({ bookingId, branchCurrency, tableName, onClose }) => {
  const [bill, setBill] = useState<Bill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tip, setTip] = useState<string>('');
  // Split the bill: when on, the customer pays a share of the outstanding amount (default = 1/people).
  const [splitOn, setSplitOn] = useState(false);
  const [people, setPeople] = useState(2);
  const [shareInput, setShareInput] = useState<string>('');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);

  const currency = bill?.currency || branchCurrency || 'INR';
  const fmt = useMemo(() => (n: number) => formatMoney(n, currency), [currency]);

  const loadBill = async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiService.getTableBill(bookingId);
      const b = normaliseBill((res as any)?.data ?? res);
      setBill(b);
      if (isPaidStatus(b.paymentStatus) || b.amountDue <= 0) setPaid(isPaidStatus(b.paymentStatus) || b.amountPaid > 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your bill.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadBill(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [bookingId]);

  // Keep the balance live while the bill is open (so a split shows others' payments landing).
  useEffect(() => {
    const iv = window.setInterval(() => { if (!paid && !paying) void loadBill(); }, 6000);
    return () => window.clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paid, paying]);

  // Return handler for the N-Genius hosted page: if we redirected out earlier, a pending payment
  // will be in localStorage. Verify it (N-Genius verifies by order reference — no signature), then
  // clear the key and reflect the resulting Paid/failed state.
  useEffect(() => {
    let pending: { tabPaymentId?: string; orderReference?: string } | null = null;
    try {
      const raw = localStorage.getItem(LS_PENDING_PAYMENT);
      if (raw) pending = JSON.parse(raw);
    } catch { pending = null; }
    if (!pending?.tabPaymentId || !pending?.orderReference) return;
    const { tabPaymentId, orderReference } = pending;
    setPaying(true); setPayError(null);
    (async () => {
      try {
        // N-Genius verify uses the order reference for both id + signature (backend ignores signature here).
        const v = await apiService.verifyTabPayment(tabPaymentId, orderReference, orderReference);
        const status = ((v as any)?.data?.status || (v as any)?.status || '').toString().toLowerCase();
        if (status && !isPaidStatus(status) && status !== 'success' && status !== 'ok') {
          setPayError('Payment could not be confirmed. If you were charged, contact staff.');
          setPaying(false);
        } else {
          finishPaid();
        }
      } catch (e) {
        setPayError(e instanceof Error ? e.message : 'Verification failed.');
        setPaying(false);
      } finally {
        try { localStorage.removeItem(LS_PENDING_PAYMENT); } catch { /* ignore */ }
      }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  /** Share the receipt via WhatsApp (wa.me deep link — opens WhatsApp to pick who to send it to). */
  const shareReceiptOnWhatsApp = () => {
    if (!bill) return;
    const lines = bill.items.map((i) => `• ${i.quantity}× ${i.name} — ${fmt(i.lineTotal)}`).join('\n');
    const text = [
      `🧾 ${tableName || 'Your bill'}`,
      lines,
      `Subtotal: ${fmt(bill.subtotal)}`,
      bill.discount > 0 ? `${bill.offerLabel || 'Discount'}: -${fmt(bill.discount)}` : '',
      bill.vatAmount > 0 ? `${vatLabel(bill.vatRate, bill.taxLabel)}: ${fmt(bill.vatAmount)}` : '',
      bill.alcoholTaxAmount > 0 ? `Alcohol tax: ${fmt(bill.alcoholTaxAmount)}` : '',
      `Total: ${fmt(bill.grandTotal)}`,
      bill.invoiceNumber ? `Invoice: ${bill.invoiceNumber}` : '',
    ].filter(Boolean).join('\n');
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const tipNum = Math.max(0, num(tip));
  // Split-the-bill amounts.
  const amountDue = bill?.amountDue || 0;
  const defaultShare = people > 0 ? Math.round((amountDue / people) * 100) / 100 : amountDue;
  const shareNum = Math.min(Math.max(0, num(shareInput)), amountDue);
  const payNow = splitOn ? (shareNum > 0 ? shareNum : defaultShare) : amountDue; // tab portion this payment covers
  // Keep the "your share" field synced to 1/people (recomputes as the remaining amount drops).
  useEffect(() => {
    if (splitOn) setShareInput(String(defaultShare));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [splitOn, people, amountDue]);

  const finishPaid = () => { setPaid(true); setPaying(false); void loadBill(); };

  const pay = async () => {
    if (paying || !bill) return;
    setPaying(true); setPayError(null);
    try {
      // Re-check the LIVE balance first, so two guests splitting can't both pay the full remainder.
      const freshRes = await apiService.getTableBill(bookingId);
      const fresh = normaliseBill((freshRes as any)?.data ?? freshRes);
      setBill(fresh);
      if (isPaidStatus(fresh.paymentStatus) || fresh.amountDue <= 0) { finishPaid(); return; }
      const share = splitOn ? Math.min(payNow, fresh.amountDue) : undefined; // never charge more than what's left
      const res = await apiService.createTabPayment(bookingId, tipNum > 0 ? tipNum : undefined, share);
      const order = ((res as any)?.data ?? res) as any;
      const isMock = order?.mock === true || order?.gateway === 'mock';
      const tabPaymentId = order?.tabPaymentId || order?.id;
      if (!tabPaymentId) throw new Error('Payment could not be started.');

      if (isMock) {
        // No real gateway configured — verify immediately with placeholder proof.
        const v = await apiService.verifyTabPayment(tabPaymentId, `mock_pay_${Date.now()}`, 'mock_signature');
        const status = ((v as any)?.data?.status || (v as any)?.status || '').toString().toLowerCase();
        if (status && !isPaidStatus(status) && status !== 'success' && status !== 'ok') {
          throw new Error('Payment could not be confirmed.');
        }
        finishPaid();
        return;
      }

      if (order?.gateway === 'razorpay' && order?.keyId) {
        const ok = await loadRazorpay();
        if (!ok || !window.Razorpay) throw new Error('Could not load the payment gateway.');
        const rzp = new window.Razorpay({
          key: order.keyId,
          amount: order.amount,
          currency: order.currency || currency,
          order_id: order.gatewayOrderId,
          name: tableName || 'Your tab',
          description: 'Table bill payment',
          handler: async (resp: any) => {
            try {
              const v = await apiService.verifyTabPayment(
                tabPaymentId,
                resp.razorpay_payment_id,
                resp.razorpay_signature,
              );
              const status = ((v as any)?.data?.status || (v as any)?.status || '').toString().toLowerCase();
              if (status && !isPaidStatus(status) && status !== 'success' && status !== 'ok') {
                setPayError('Payment could not be confirmed. If you were charged, contact staff.');
                setPaying(false);
                return;
              }
              finishPaid();
            } catch (e) {
              setPayError(e instanceof Error ? e.message : 'Verification failed.');
              setPaying(false);
            }
          },
          modal: { ondismiss: () => { setPaying(false); } },
          theme: { color: '#16a34a' },
        });
        rzp.on('payment.failed', (resp: any) => {
          setPayError(resp?.error?.description || 'Payment failed. Please try again.');
          setPaying(false);
        });
        rzp.open();
        return;
      }

      // UAE / N-Genius hosted payment page. Persist the pending payment so we can verify on return,
      // then hand off to the hosted page (which redirects back to the app when done).
      const paymentPageUrl = order?.paymentPageUrl;
      const orderReference = order?.orderReference;
      if (paymentPageUrl && orderReference) {
        try {
          localStorage.setItem(LS_PENDING_PAYMENT, JSON.stringify({ tabPaymentId, orderReference }));
        } catch { /* ignore storage failures */ }
        window.location.href = paymentPageUrl;
        return;
      }

      throw new Error('This payment method is not available right now. Please pay at the counter.');
    } catch (e) {
      setPayError(e instanceof Error ? e.message : 'Payment failed.');
      setPaying(false);
    }
  };

  const dueVisible = !!bill && bill.amountDue > 0 && !isPaidStatus(bill.paymentStatus) && !paid;

  return (
    <div className="ui-scope" style={overlay} onClick={onClose}>
      <UiStyles />
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <ReceiptText size={18} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>Your bill</div>
              <div style={subLabel}>{tableName || 'Table tab'}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={iconBtn} aria-label="Close"><X size={20} /></button>
        </div>

        <div style={body}>
          {/* Stronger "save this" nudge at the moment it matters most — the bill. A guest can log in
              to keep this receipt (and any rewards) on their registered account. Guest sessions only. */}
          {apiService.getGuestAccessToken() && (
            <button
              type="button"
              onClick={() => beginGuestSessionClaim()}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                padding: '12px 14px', marginBottom: 12, borderRadius: 12, cursor: 'pointer',
                border: '1px solid #e0531f', background: '#e0531f', color: '#ffffff',
              }}
            >
              <span aria-hidden style={{ fontSize: '1.15rem' }}>🎁</span>
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontWeight: 700, fontSize: '0.92rem' }}>Log in to keep this receipt &amp; earn rewards</span>
                <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.9 }}>Your order &amp; tab move to your account — takes a few seconds</span>
              </span>
            </button>
          )}
          {loading ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '1.5rem' }}>Loading your bill…</p>
          ) : error ? (
            <div>
              <div style={errorBox}>{error}</div>
              <button style={retryBtn} onClick={() => void loadBill()}>Try again</button>
            </div>
          ) : bill ? (
            <>
              {bill.items.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '0.9rem', padding: '4px 0' }}>No items on this tab yet.</p>
              ) : (
                <div style={{ marginBottom: 12 }}>
                  {bill.items.map((it, i) => (
                    <div key={i} style={lineRow}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.92rem', fontWeight: 600, color: '#111827' }}>{it.name}</div>
                        <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{it.quantity} × {fmt(it.unitPrice)}</div>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>{fmt(it.lineTotal)}</div>
                    </div>
                  ))}
                </div>
              )}

              <div style={totalsBox}>
                <Row label="Subtotal" value={fmt(bill.subtotal)} />
                {bill.discount > 0 && <Row label={bill.offerLabel || 'Discount'} value={`- ${fmt(bill.discount)}`} accent="#15803d" />}
                {bill.tax > 0 && <Row label="Tax" value={fmt(bill.tax)} />}
                {bill.vatAmount > 0 && <Row label={vatLabel(bill.vatRate, bill.taxLabel)} value={fmt(bill.vatAmount)} />}
                {bill.alcoholTaxAmount > 0 && <Row label={`Alcohol tax${bill.alcoholTaxRate > 0 ? ` (${Math.round(bill.alcoholTaxRate * 100)}%)` : ''}`} value={fmt(bill.alcoholTaxAmount)} />}
                <div style={divider} />
                <Row label="Grand total" value={fmt(bill.grandTotal)} strong />
                {bill.amountPaid > 0 && <Row label="Paid" value={fmt(bill.amountPaid)} accent="#15803d" />}
                <Row label="Amount due" value={fmt(bill.amountDue)} strong accent={bill.amountDue > 0 ? '#b91c1c' : '#15803d'} />
              </div>

              {(bill.legalEntity || bill.trn || bill.invoiceNumber) && (
                <div style={legalBox}>
                  {bill.invoiceNumber && <div>Invoice: {bill.invoiceNumber}</div>}
                  {bill.trn && <div>TRN: {bill.trn}</div>}
                  {bill.legalEntity && <div>{bill.legalEntity}</div>}
                </div>
              )}

              <div style={{ marginTop: 10, textAlign: 'center' }}>
                <span style={{ ...statusPill, ...(paid || isPaidStatus(bill.paymentStatus) ? paidPill : bill.amountDue > 0 ? duePill : neutralPill) }}>
                  {paid || isPaidStatus(bill.paymentStatus) ? 'Paid ✓' : (bill.paymentStatus || (bill.amountDue > 0 ? 'Unpaid' : 'Settled'))}
                </span>
              </div>

              {bill.items.length > 0 && (
                <button type="button" onClick={shareReceiptOnWhatsApp} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', marginTop: 10, padding: '11px', borderRadius: 12, border: '1px solid #25D366', background: '#e8faf0', color: '#128C4B', fontWeight: 700, cursor: 'pointer' }}>
                  💬 Share receipt on WhatsApp
                </button>
              )}

              {(paid || isPaidStatus(bill.paymentStatus)) && (
                <div style={paidBanner}>
                  <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>Paid ✓</div>
                  <div style={{ fontSize: '0.85rem', marginTop: 2 }}>Thanks! Your tab is settled.</div>
                  <PayFeedback bookingId={bookingId} />
                  <button style={{ ...payBtn, marginTop: 10, background: '#111827' }} onClick={onClose}>Close</button>
                </div>
              )}

              {dueVisible && (
                <div style={{ marginTop: 14 }}>
                  {/* Split the bill — pay only your share; the tab draws down as each person pays. */}
                  <div style={splitCard}>
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 800, color: '#111827' }}>👥 Split the bill</span>
                      <input type="checkbox" checked={splitOn} onChange={(e) => setSplitOn(e.target.checked)} style={{ width: 18, height: 18 }} />
                    </label>
                    {splitOn && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontSize: '0.85rem', color: '#374151' }}>Sharing between</span>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <button type="button" style={stepBtn} onClick={() => setPeople((p) => Math.max(2, p - 1))}>−</button>
                            <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 800 }}>{people}</span>
                            <button type="button" style={stepBtn} onClick={() => setPeople((p) => Math.min(50, p + 1))}>＋</button>
                            <span style={{ fontSize: '0.85rem', color: '#374151' }}>people</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>You pay ({currency})</span>
                          <input style={tipInput} type="number" min={0} inputMode="decimal" value={shareInput} onChange={(e) => setShareInput(e.target.value)} />
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 6 }}>
                          {fmt(bill.amountDue)} due · your share ≈ {fmt(defaultShare)}. Each person pays their share until the tab is settled.
                        </div>
                      </div>
                    )}
                  </div>

                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                    Add a tip (optional)
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>{currency}</span>
                    <input
                      style={tipInput}
                      type="number"
                      min={0}
                      inputMode="decimal"
                      placeholder="0"
                      value={tip}
                      onChange={(e) => setTip(e.target.value)}
                    />
                  </div>
                  {payError && <div style={errorBox}>{payError}</div>}
                  <button style={{ ...payBtn, opacity: paying || (splitOn && payNow <= 0) ? 0.6 : 1 }} disabled={paying || (splitOn && payNow <= 0)} onClick={() => void pay()}>
                    {paying ? 'Processing…' : `Pay ${splitOn ? 'my share' : 'now'} · ${fmt((splitOn ? payNow : bill.amountDue) + (tipNum > 0 ? tipNum : 0))}`}
                  </button>
                  <div style={venueNote}>You can also pay at the counter.</div>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: string; strong?: boolean; accent?: string }> = ({ label, value, strong, accent }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
    <span style={{ fontSize: strong ? '0.95rem' : '0.85rem', fontWeight: strong ? 800 : 500, color: strong ? '#111827' : '#4b5563' }}>{label}</span>
    <span style={{ fontSize: strong ? '0.98rem' : '0.88rem', fontWeight: strong ? 800 : 600, color: accent || '#111827' }}>{value}</span>
  </div>
);

const splitCard: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 12px', marginBottom: 12 };
const stepBtn: React.CSSProperties = { width: 30, height: 30, borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', color: '#111827', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1300 };
const panel: React.CSSProperties = { background: '#fff', color: '#111827', borderTopLeftRadius: 16, borderTopRightRadius: 16, width: 'min(520px, 100vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -6px 30px rgba(0,0,0,0.25)' };
const header: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #e5e7eb', gap: 8 };
const subLabel: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const body: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: '14px', background: '#f9fafb' };
const lineRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', background: '#fff', border: '1px solid #eef2f7', borderRadius: 10, marginBottom: 6 };
const totalsBox: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 12px' };
const divider: React.CSSProperties = { height: 1, background: '#e5e7eb', margin: '6px 0' };
const statusPill: React.CSSProperties = { display: 'inline-block', borderRadius: 9999, padding: '4px 14px', fontSize: '0.8rem', fontWeight: 800 };
const paidPill: React.CSSProperties = { background: '#dcfce7', color: '#166534' };
const duePill: React.CSSProperties = { background: '#fee2e2', color: '#b91c1c' };
const neutralPill: React.CSSProperties = { background: '#f3f4f6', color: '#6b7280' };
const paidBanner: React.CSSProperties = { marginTop: 14, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 12, padding: '14px', textAlign: 'center' };
const tipInput: React.CSSProperties = { flex: 1, boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 10, padding: '10px 12px', fontSize: '0.95rem', outline: 'none', color: '#111827', background: '#fff' };
const payBtn: React.CSSProperties = { width: '100%', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 12, padding: '13px 16px', fontWeight: 800, fontSize: '1rem', cursor: 'pointer' };
const venueNote: React.CSSProperties = { textAlign: 'center', fontSize: '0.8rem', color: '#6b7280', marginTop: 10 };
const legalBox: React.CSSProperties = { marginTop: 10, textAlign: 'center', fontSize: '0.72rem', lineHeight: 1.5, color: '#9ca3af' };
const errorBox: React.CSSProperties = { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 10, padding: '8px 12px', fontSize: '0.82rem', marginBottom: 10 };
const retryBtn: React.CSSProperties = { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 10, padding: '9px 16px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' };

export default BillModal;
